// Drawola Studio — display formatting helpers. Fork-only.

import type { StudioTranslate } from "./i18n";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "just now" / "12m ago" / "3h ago" / "5d ago" / an absolute date past a week.
 * Mirrors the buckets the cloud file picker already uses so the two lists read
 * the same, but with its own `studio.time.*` keys.
 */
export const formatRelativeTime = (
  timestamp: number,
  t: StudioTranslate,
): string => {
  const delta = Date.now() - timestamp;

  if (!Number.isFinite(timestamp) || delta < MINUTE) {
    return t("studio.time.justNow");
  }
  if (delta < HOUR) {
    return t("studio.time.minutesAgo", { count: Math.floor(delta / MINUTE) });
  }
  if (delta < DAY) {
    return t("studio.time.hoursAgo", { count: Math.floor(delta / HOUR) });
  }
  if (delta < 7 * DAY) {
    return t("studio.time.daysAgo", { count: Math.floor(delta / DAY) });
  }
  try {
    return new Date(timestamp).toLocaleDateString();
  } catch {
    return t("studio.time.justNow");
  }
};

/** Characters browsers / filesystems reject in a download file name. */
// eslint-disable-next-line no-control-regex
const ILLEGAL_FILENAME_CHARS = /[\u0000-\u001f<>:"/\\|?*]/g;

/**
 * Make `<name>.drawola` a usable download file name: strip illegal
 * characters, collapse whitespace, drop trailing dots, cap the length.
 */
export const toFileName = (name: string, fallback: string): string => {
  const cleaned = name
    .replace(ILLEGAL_FILENAME_CHARS, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\.+$/, "")
    .slice(0, 120)
    .trim();
  return cleaned || fallback;
};
